import { parseColor } from '@betgames/bg-tools';
import { isColorLight } from 'twain-sport-fe-mono';
import { Theme } from 'enums/Theme';

type ICallback = () => void;

type ICssVars = `--${string}`[];

export class AssetsService {
    private theme = Theme.DEFAULT;

    // css variables should exist in `styles/themes/default.global.scss`
    private cssVarsKeys: ICssVars = [
        '--text-dark',
        '--text-light',
        '--text',
        '--text-neutral',
        '--text-neutral-light',
        '--page-background',
        '--entity-background',
        '--entity-item-background',
        '--primary',
        '--secondary',
        '--secondary-light',
        '--color-chart-gradient',
        '--betting-option-main',
        '--color-rng-right',
        '--color-rng-left',
    ];

    // text `--text-on-X`
    private cssVarsKeysTextColor: ICssVars = [
        '--primary',
        '--secondary',
        '--secondary-light',
        '--success',
        '--warning',
        '--error',
    ];

    // text `--text-on-X-rgb`
    private cssVarsKeysTextColorRgb: ICssVars = ['--success'];

    private callbacks = new Set<ICallback>();

    // eslint-disable-next-line class-methods-use-this
    public initCustomCss = (theme: Theme, customCss: string) => {
        const styleTag =
            document.querySelector('[data-link-css]') ?? document.createElement('style');

        if ([Theme.DARK, Theme.LIGHT].includes(theme)) {
            styleTag.innerHTML = customCss.replace(':root', `.${theme}:root`);
        } else {
            styleTag.innerHTML = customCss;
        }

        styleTag.setAttribute('type', 'text/css');
        styleTag.setAttribute('data-link-css', '');
        document.body.appendChild(styleTag);
    };

    public setTheme = (theme: Theme) => {
        document.documentElement.classList.remove(Theme.DARK, Theme.LIGHT, Theme.DEFAULT);
        if ([Theme.DARK, Theme.LIGHT].includes(theme)) {
            document.documentElement.classList.add(theme);
        }
        this.theme = theme;
        this.generateCssVarModifications();

        this.callbacks.forEach((callback) => {
            callback();
        });
    };

    public getTheme = () => this.theme;

    public subscribeOnChange(callback: ICallback): ICallback {
        this.callbacks.add(callback);

        return () => {
            this.callbacks.delete(callback);
        };
    }

    // generates RGB values from computed value and sets to html element, e.g. for css variable
    //   --some-var: #fff;
    // will be generated:
    //   --some-var-rgb: 255,255,255;
    public generateCssVarModifications = (): void => {
        const computedStyles = getComputedStyle(document.body);

        this.cssVarsKeys.forEach((key) => {
            const value = computedStyles.getPropertyValue(key);
            const valueRgb = parseColor(value.trim());

            if (valueRgb) {
                document.documentElement.style.setProperty(
                    `${key}-rgb`,
                    valueRgb.slice(0, 3).join(','),
                );
            }
        });

        this.cssVarsKeysTextColor.forEach((key) => {
            const value = computedStyles.getPropertyValue(key);
            const isLight = isColorLight(value.trim());
            const textKey = `--text-on-${key.substring(2)}`;
            const definedTextColor = computedStyles.getPropertyValue(textKey);

            if (!definedTextColor) {
                document.documentElement.style.setProperty(
                    textKey,
                    isLight ? 'var(--text-dark)' : 'var(--text-light)',
                );
            }

            if (this.cssVarsKeysTextColorRgb.includes(key)) {
                let textColor = isLight ? 'var(--text-dark-rgb)' : 'var(--text-light-rgb)';

                if (definedTextColor) {
                    textColor = parseColor(definedTextColor.trim()).slice(0, 3).join(',');
                }

                document.documentElement.style.setProperty(`${textKey}-rgb`, textColor);
            }
        });
    };
}
